import { validateString } from "../string/stringUtil";
import { showError, showWarning } from "../toast/toastUtil";


export const refetchUpdateList = async <T extends Record<string, any>>(
	processName: string,
	responseData: T,
	itemToUpdate: T | null | undefined,
	primaryKey: keyof T,
	currentList: T[],
	setList: (list: T[]) => void,
	moreOrderCellArray?: string[] | undefined
) => {
	// try {
		// console.log('itemToUpdate : ', itemToUpdate);
		// console.log('responseData : ', JSON.stringify(responseData));
		// console.log('processName : ', processName);
		// console.log('primaryKey : ', primaryKey);

		let itemToUpdateKey = itemToUpdate?.[primaryKey] as string; //1순위로 edit한 곳에서 primary key를 찾음
		let keyToFind = '';
		if (validateString(itemToUpdateKey)) {
			keyToFind = itemToUpdateKey; 
		}else {
			keyToFind = responseData[primaryKey] as string; //2순위로 responseData에서 primary key를 찾음
		}
		if (!validateString(keyToFind)) {
			showWarning('식별값에 해당하는 값이 목록에서 없어 목록에서 내용을 확인할수 없습니다.');
			return false;
		}

		// console.log('processName : ', processName);
		// console.log('keyToFind : ', keyToFind);
		// console.log('responseData : ', JSON.stringify(responseData));
		// console.log('responseData[primaryKey] : ', responseData[primaryKey]);

		let newList: T[];

		if (['patch', 'update', 'put', 'modify'].includes(processName)) {
			newList = currentList.map(item =>
				item[primaryKey] === keyToFind
					? responseData
					: item
			);
		}
		else if (['post', 'create', 'insert'].includes(processName)) {
			newList = [responseData, ...currentList];
		}
		else if (['delete', 'remove'].includes(processName)) {
			newList = currentList.filter(item => item[primaryKey] !== keyToFind);
		} else {
			newList = [...currentList];
		}

		if (moreOrderCellArray && moreOrderCellArray.length > 0) {
			newList.sort((a, b) => {
				for (const key of moreOrderCellArray) {
					const valueA = a[key];
					const valueB = b[key];
					if (valueA < valueB) return -1;
					if (valueA > valueB) return 1;
				}
				return 0;
			});
		}

		setList(newList);

	// } catch (error) {
	// 	showError("목록을 리패치하고 업데이트하는 중 오류가 발생했습니다:", 2500);
	// }

	return true;
};

/**
 * 저장 후 여러 항목(신규, 수정, 삭제)을 그리드에 반영하는 핸들러 함수입니다.
 * 엑셀과 같이 여러 행을 동시에 편집하는 그리드에 사용됩니다.
 * 처리 순서: 신규 항목 반영 -> 수정 항목 반영 -> 삭제 항목 반영
 * 
 * @template T - 목록에 포함된 항목의 데이터 타입 (e.g., SYS4100_R01Dto). 
 *               `valueIndex`, `inserted`, `isModified` 와 같은 속성을 포함해야 합니다.
 * @param insertedResponse - 신규 항목 추가 API의 응답. `data.data`에 생성된 항목 배열이 있어야 합니다.
 * @param modifiedResponse - 항목 수정 API의 응답. `data.data`에 수정된 항목 배열이 있어야 합니다.
 * @param deletedResponse - 항목 삭제 API의 응답. 길이를 확인하여 삭제 작업 성공 여부를 판단합니다.
 * @param deletedIdList - 삭제를 위해 API에 전달된 ID 목록 (`valueIndex` 기준).
 * @param currentList - 현재 그리드의 전체 목록 배열.
 * @param setList - 그리드 목록 상태를 업데이트하는 setter 함수.
 * @param primaryKey - 수정된 항목을 식별하기 위한 고유 키 이름 (e.g., 'orgCd').
 */
export const refetchUpdateList_Excelgrid = <T extends { [key: string]: any; valueIndex: number; inserted?: boolean; isModified?: boolean; }>({
    insertedResponse,
    modifiedResponse,
    deletedIdList,
    currentList,
    setList,
    primaryKey,
}: {
    insertedResponse?: { data?: { data?: T[] } };
    modifiedResponse?: { data?: { data?: T[] } };
    deletedIdList: (string | number)[];
    currentList: T[];
    setList: (list: T[]) => void;
    primaryKey: keyof T;
}) => {
    let newList = [...currentList];

    // 1. 신규 항목 처리: 로컬의 임시 행(inserted: true)을 서버에서 받은 최종 데이터로 교체합니다.
    if (insertedResponse?.data?.data && insertedResponse.data.data.length > 0) {
        const serverInsertedItems = insertedResponse.data.data;
        // 임시 행들을 제거합니다.
        newList = newList.filter(item => !item.inserted);
        // 서버에서 받은 최종 데이터를 추가하고 상태 플래그를 초기화합니다.
        newList.push(...serverInsertedItems.map(item => ({ ...item, inserted: false, isModified: false })));
    }

    // 2. 수정 항목 처리: 수정된 항목을 서버에서 받은 최신 데이터로 업데이트합니다.
    if (modifiedResponse?.data?.data && modifiedResponse.data.data.length > 0) {
        const serverModifiedItems = modifiedResponse.data.data;
        const modifiedItemsMap = new Map(serverModifiedItems.map(item => [item[primaryKey], item]));
        
        newList = newList.map(currentItem => {
            const updatedItemFromServer = modifiedItemsMap.get(currentItem[primaryKey]);
            if (updatedItemFromServer) {
                // 기존 데이터와 서버 데이터를 병합하고 상태 플래그를 초기화합니다.
                return { ...currentItem, ...updatedItemFromServer, isModified: false };
            }
            return currentItem;
        });
    }

    // 3. 삭제 항목 처리: 삭제 목록에 있는 항목들을 그리드에서 제거합니다.
    if (deletedIdList.length > 0) {
        const idsToDelete = new Set(deletedIdList.map(id => String(id)));
        newList = newList.filter(item => !idsToDelete.has(String(item.valueIndex)));
    }

    setList(newList);
};